import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../utils/api';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import TimezoneSelector from './TimezoneSelector';

interface Caregiver {
  id?: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  timezone: string;
  alert_order: number;
}

const EditCareCircle: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [caregivers, setCaregivers] = useState<Caregiver[]>([]);

  // Load existing care circle data
  useEffect(() => {
    const loadCareCircleData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await fetch(`${getApiUrl()}/caregiver-circles/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.caregivers) {
            setCaregivers(data.caregivers.map((caregiver: any) => ({
              id: caregiver.id,
              first_name: caregiver.first_name || '',
              last_name: caregiver.last_name || '',
              phone_number: caregiver.phone_number || '',
              timezone: caregiver.timezone || 'America/Los_Angeles',
              alert_order: caregiver.alert_order || 1,
            })));
          } else {
            // If no care circle exists, create a default one with the current user
            const userResponse = await fetch(`${getApiUrl()}/users/me`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              setCaregivers([{
                first_name: userData.full_name?.split(' ')[0] || '',
                last_name: userData.full_name?.split(' ').slice(1).join(' ') || '',
                phone_number: userData.phone_number || '',
                timezone: userData.timezone || 'America/Los_Angeles',
                alert_order: 1,
              }]);
            }
          }
        } else {
          setError('Failed to load care circle information');
        }
      } catch (err) {
        setError('Error loading care circle information');
        console.error('Error loading care circle data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCareCircleData();
  }, [navigate]);

  const addCaregiver = () => {
    if (caregivers.length >= 4) return;
    
    const newOrder = caregivers.length + 1;
    setCaregivers(prev => [...prev, {
      first_name: '',
      last_name: '',
      phone_number: '',
      timezone: 'America/Los_Angeles',
      alert_order: newOrder,
    }]);
  };

  const removeCaregiver = (index: number) => {
    if (caregivers.length <= 1) return;
    
    setCaregivers(prev => {
      const newCaregivers = prev.filter((_, i) => i !== index);
      // Reorder remaining caregivers
      return newCaregivers.map((caregiver, i) => ({
        ...caregiver,
        alert_order: i + 1,
      }));
    });
  };

  const updateCaregiver = (index: number, field: keyof Caregiver, value: any) => {
    setCaregivers(prev => prev.map((caregiver, i) => 
      i === index ? { ...caregiver, [field]: value } : caregiver
    ));
  };

  const validateForm = () => {
    if (caregivers.length === 0) {
      setError('At least one caregiver is required');
      return false;
    }

    for (let i = 0; i < caregivers.length; i++) {
      const caregiver = caregivers[i];
      if (!caregiver.first_name.trim() || !caregiver.last_name.trim() || !caregiver.phone_number.trim()) {
        setError(`Caregiver ${i + 1}: First name, last name, and phone number are required`);
        return false;
      }

      // Basic phone number validation
      const phoneRegex = /^\+?1?\s*\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
      if (!phoneRegex.test(caregiver.phone_number.trim())) {
        setError(`Caregiver ${i + 1}: Please enter a valid phone number (e.g., +1 (555) 123-4567)`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${getApiUrl()}/caregiver-circles/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          caregivers: caregivers.map(caregiver => ({
            id: caregiver.id,
            first_name: caregiver.first_name.trim(),
            last_name: caregiver.last_name.trim(),
            phone_number: caregiver.phone_number.trim(),
            timezone: caregiver.timezone,
            alert_order: caregiver.alert_order,
          })),
        }),
      });

      if (response.ok) {
        setSuccess('Care circle updated successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update care circle');
      }
    } catch (err) {
      setError('Error updating care circle');
      console.error('Error updating care circle:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" color="primary">
          Edit Care Circle
        </Typography>
        
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Manage who receives escalation alerts when your loved one doesn't respond
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>How it works:</strong> If your loved one doesn't respond to a check-in, 
            we'll contact caregivers in order. If the first person doesn't respond, 
            we'll try the next one, and so on.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {caregivers.map((caregiver, index) => (
            <Card key={index} sx={{ mb: 3, p: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="primary">
                    Caregiver {index + 1}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={`Alert Order: ${caregiver.alert_order}`} 
                      color="secondary" 
                      size="small" 
                    />
                    {caregivers.length > 1 && (
                      <IconButton
                        onClick={() => removeCaregiver(index)}
                        color="error"
                        size="small"
                        disabled={saving}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="First Name"
                      value={caregiver.first_name}
                      onChange={(e) => updateCaregiver(index, 'first_name', e.target.value)}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      required
                      fullWidth
                      label="Last Name"
                      value={caregiver.last_name}
                      onChange={(e) => updateCaregiver(index, 'last_name', e.target.value)}
                      disabled={saving}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Phone Number"
                      type="tel"
                      value={caregiver.phone_number}
                      onChange={(e) => updateCaregiver(index, 'phone_number', e.target.value)}
                      disabled={saving}
                      helperText="We'll send escalation alerts to this number"
                      placeholder="+1 (555) 123-4567"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TimezoneSelector
                      onTimezoneChange={(timezone) => updateCaregiver(index, 'timezone', timezone)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}

          {caregivers.length < 4 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Button
                startIcon={<AddIcon />}
                onClick={addCaregiver}
                disabled={saving}
                variant="outlined"
                color="primary"
              >
                Add Another Caregiver
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              disabled={saving}
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              sx={{ minWidth: 120 }}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditCareCircle;
